import { FC, useContext, useEffect, useState } from "react"

import { Col, Row, Statistic, Space, Tag, Typography } from "antd"
import moment from "moment"

import { useAnalyticsHelpers } from "@/components/Analytics/useAnalyticsHelpers"
import { CustomerContext } from "@/context/CustomerContext"

import { FilterType, IAnalyticsSettingsOptions } from "@/types/IAnalytics"

import { formatNumber } from "@/utils/formatNumber"

const { Text } = Typography

export interface StatisticItem {
  title: string
  value: number
}

export interface AnalyticsFilterInfoProps {
  optionsList: IAnalyticsSettingsOptions[]
  filterTypes?: FilterType[] | undefined
  statistics?: StatisticItem[]
}

interface AnalyticsFilterInfoOption {
  type: FilterType,
  label: string,
  value: string,
  color?: "red" | "green" | "purple" | "blue" | "orange" | "cyan" | "gold" | "lime" | "magenta" | "pink" | "yellow" | "volcano" | "geekblue" | undefined
}

interface AnalyticsFilterInfoOptions {
  filterOptions: AnalyticsFilterInfoOption[]
}

export const useAnalyticsFilterInfo = (props: AnalyticsFilterInfoProps) => {
  const {
    optionsList,
    statistics,
    filterTypes
  } = props


  const { parseDateFromStringStartFromYear } = useAnalyticsHelpers({})

  const [filterInfoOptions, setFilterInfoOptions] = useState<AnalyticsFilterInfoOptions[]>([])
  const [filterInfoAsText, setFilterInfoAsText] = useState<string[]>([])

  const customerContext = useContext(CustomerContext)

  useEffect(() => {
    const filterOptionsList: AnalyticsFilterInfoOptions[] = []
    optionsList.forEach(option => {
      filterOptionsList.push({ filterOptions: mapFilterInfoOptions(option) })
    })
    setFilterInfoOptions(filterOptionsList)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [optionsList])

  useEffect(() => {
    setFilterInfoAsText(getFilterInfoAsPlainText())
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterInfoOptions])

  const hasFiltersApplied: boolean | undefined = 
    filterTypes && filterTypes?.length > 0 && optionsList && optionsList.some(option =>
      option.deliveryType !== undefined ||
      option.fuelTypeId !== undefined ||
      option.depotId !== undefined ||
      option.customerClientId !== undefined ||
      option.customerCategoryId !== undefined ||
      option.startDate !== undefined ||
      option.endDate !== undefined ||
      option.activePeriod !== undefined
    )
  

  const mapFilterInfoOptions = (options: IAnalyticsSettingsOptions): AnalyticsFilterInfoOption[] => {
    if (!hasFiltersApplied || !filterTypes)
      return []

    const filterOptions: AnalyticsFilterInfoOption[] = []

    filterTypes.forEach(type => {
      const filterOption: AnalyticsFilterInfoOption = {
        type: FilterType.Period,
        label: "Период:",
        color: "red",
        value: ""
      }
      switch (type) {
        case FilterType.Period:
        default:
          if (options.activePeriod || (options.startDate && options.endDate)) {
            filterOption.type = FilterType.Period
            filterOption.label = "Период:"
            filterOption.color = "red"
            if (options.activePeriodName) {
              filterOption.value = `за ${options.activePeriodName ?? options.activePeriod?.toString()}`
            }
            if (options.startDate && options.endDate) {
              // eslint-disable-next-line max-len
              filterOption.value = `${moment(parseDateFromStringStartFromYear(options.startDate, "-")).format("DD.MM.YYYY")}-${moment(parseDateFromStringStartFromYear(options.endDate, "-")).format("DD.MM.YYYY")}`
            }
          }
          break

        case FilterType.Fuel:
          if (options.fuelTypeId) {
            filterOption.type = FilterType.Fuel
            filterOption.label = "Вид топлива:"
            filterOption.color = "blue"
            filterOption.value = `${options.fuelTypeName ?? options.fuelTypeId}`
          }
          break

        case FilterType.Depot:
          if (options.depotId) {
            filterOption.type = FilterType.Depot
            filterOption.label = "Нефтебаза:"
            filterOption.color = "green"
            filterOption.value = `${options.depotName ?? options.depotId}`
          }
          break

        case FilterType.DeliveryType:
          if (options.deliveryType) {
            filterOption.type = FilterType.DeliveryType
            filterOption.label = "Тип доставки:"
            filterOption.color = "cyan"
            filterOption.value = `${options.deliveryTypeName ?? options.deliveryType.toString()}`
          }
          break

        case FilterType.Customer:
          if (!customerContext.isCustomer() && options.customerClientId) {
            filterOption.type = FilterType.Customer
            filterOption.label = "Контрагент:"
            filterOption.color = "orange"
            filterOption.value = `${options.customerClientName ?? options.customerClientId}`
          }
          break

        case FilterType.CustomerCategory:
          if (!customerContext.isCustomer() && options.customerCategoryId) {
            filterOption.type = FilterType.CustomerCategory
            filterOption.label = "Категория:"
            filterOption.color = "purple"
            filterOption.value = `${options.customerCategoryName ?? options.customerCategoryId}`
          }
          break
      }

      if (filterOption.value && filterOption.value !== "") {
        filterOptions.push(filterOption)
      }
    })
    return filterOptions
  }

  const getFilterInfoAsPlainText = (noTypes: FilterType[] = []): string[] => {
    const labelArr: string[] = []
    if (filterInfoOptions.length > 0) {

      filterInfoOptions.forEach(f => {
        const infos: string[] = []
        f.filterOptions.forEach(ff => {
          if (noTypes.find(t => t === ff.type) === undefined) {
            infos.push(`${ff.label} ${ff.value}`)
          }
        })
        if (infos.length > 0) {
          labelArr.push(infos.join(", "))
        }
      })

    }
    return labelArr
  }

  const filterInfoComponent = <AnalyticsFilterInfo statistics={statistics} options={filterInfoOptions}/>

  return {
    hasFiltersApplied,
    filterInfoAsText,
    filterInfoComponent,
    filterInfoOptions,
    getFilterInfoAsPlainText
  }
}

export interface AnalyticsFilterInfoComponentProps {
  options: AnalyticsFilterInfoOptions[]
  statistics?: StatisticItem[]
}

export const AnalyticsFilterInfo: FC<AnalyticsFilterInfoComponentProps> = ({
  options,
  statistics
}: AnalyticsFilterInfoComponentProps) => {

  if (options.length === 0) {
    return null
  }

  return (
    <Row gutter={[16, 16]} style={{ marginBottom: "24px" }}>
      <Col span={24}>
        {statistics && statistics.length > 0 && (
          <Row gutter={[16, 16]}>
            {statistics.map((stat, index) => (
              <Col key={index} xs={24} sm={12} md={8}>
                <Statistic
                  title={stat.title}
                  value={Number.isInteger(stat.value) ? stat.value : formatNumber(stat.value)}
                  valueStyle={{ color: "#1890ff", fontSize: "24px", fontWeight: "bold" }}
                />
              </Col>
            ))}
          </Row>
        ) }
        {!statistics && (
          <Text>Фильтры:</Text>
        )}
      </Col>
      <Col span={24}>
        {options.map((option, index) => (
          <div key={"filter-info" + index} style={{ marginBottom: "16px" }}>
            {options.length > 1 && (
              <div style={{ marginBottom: "8px", fontWeight: "bold" }}>
                Период {index + 1}:
              </div>
            )}
            <Space wrap>
              {option.filterOptions.map(tag => (
                <Tag color={tag.color ?? "blue"} key={"filter-tag-"+ crypto.randomUUID()}>
                  {tag.label} {tag.value}
                </Tag>
              ))}
            </Space>
          </div>
        ))}
      </Col>
    </Row>
  )
}